import axios from '../../sharedModules/utils/axios.utils';

export interface WarehouseOption {
  wh_id: string;
  name: string;
  // Present on favorites (the server labels which display group they came from),
  // and assigned client-side when flattening the grouped lists.
  group?: string;
}

export interface WarehouseGroup {
  group: string;
  warehouses: WarehouseOption[];
}

export interface WarehouseListResponse {
  // Per-user, unlike favorites: resolved from the signed-in user's ServMan-assigned
  // warehouse. Null when they have none in the selected location.
  my_truck?: WarehouseOption | null;
  favorites: WarehouseOption[];
  groups: WarehouseGroup[];
}

export const MY_TRUCK_GROUP = 'My Truck';
export const FAVORITES_GROUP = 'Favorites';

// The server owns ordering. Flattening preserves it so MUI's groupBy renders
// My Truck first, then Favorites, then the alphabetical groups, with no
// client-side sorting. The assigned warehouse also still appears in its own
// group — My Truck is a shortcut to it, not a replacement for it.
export const flattenWarehouseOptions = (response: WarehouseListResponse): WarehouseOption[] => [
  ...(response.my_truck ? [{ ...response.my_truck, group: MY_TRUCK_GROUP }] : []),
  ...response.favorites.map((warehouse) => ({ ...warehouse, group: FAVORITES_GROUP })),
  ...response.groups.flatMap((group) => group.warehouses.map((warehouse) => ({ ...warehouse, group: group.group }))),
];

// Favorites are suppressed from the server's groups; this is the inverse of flatten
// so the options page can show every warehouse, favorited or not.
export const groupsWithFavorites = (data: WarehouseListResponse): WarehouseGroup[] => {
  const byGroup = new Map<string, WarehouseOption[]>();
  data.groups.forEach((group) => byGroup.set(group.group, [...group.warehouses]));
  data.favorites.forEach((favorite) => {
    const name = favorite.group || 'Other';
    const list = byGroup.get(name) ?? [];
    list.push(favorite);
    byGroup.set(name, list);
  });

  return Array.from(byGroup.entries())
    .map(([group, warehouses]) => ({
      group,
      warehouses: [...warehouses].sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.group.localeCompare(b.group));
};

export const fetchWarehouseList = async (location: string): Promise<WarehouseListResponse> => {
  const response = await axios.get<WarehouseListResponse>('/receiving/warehouses', { params: { location } });
  return response.data;
};
